import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import {
  formatMinutesAsDuration,
  formatDueInstant,
  formatPolicyMinutesRow,
  formatPolicyDiffRows,
  formatTatAuditActionShort,
  buildTatAuditSummary,
  getTatAuditVisibleChanges,
  getTatAuditDetailTableRows,
  isTatAuditScheduledAgainstCurrent,
  toDatetimeLocalValue,
  datetimeLocalToIso,
  defaultScheduleEffectiveLocal,
  minScheduleDatetimeLocal,
  validateScheduleEffectiveLocal,
  validateTatPolicyMinutes,
  sanitizeTatMinutesInput,
  parseTatPolicyMinutesFromForm,
  TAT_SCHEDULE_MIN_LEAD_MINUTES,
} from '../../../utils/complaintTatFormatter';
import './ComplaintTatSettings.css';

const SEVERITY_FIELDS = [
  { key: 'critical', label: 'Critical' },
  { key: 'major', label: 'Major' },
  { key: 'minor', label: 'Minor' },
];

const emptyForm = () => ({
  effective_from: '',
  critical_ack_minutes: 30,
  critical_closure_minutes: 10080,
  major_ack_minutes: 120,
  major_closure_minutes: 14400,
  minor_ack_minutes: 480,
  minor_closure_minutes: 20160,
  note: '',
});

function versionToForm(version, defaultEffectiveLocal) {
  if (!version) return { ...emptyForm(), effective_from: defaultEffectiveLocal };
  let effectiveLocal = defaultEffectiveLocal;
  if (version.effective_from) {
    const d = new Date(version.effective_from);
    if (!Number.isNaN(d.getTime())) {
      effectiveLocal = toDatetimeLocalValue(d);
    }
  }
  return {
    effective_from: effectiveLocal,
    critical_ack_minutes: version.critical_ack_minutes ?? 30,
    critical_closure_minutes: version.critical_closure_minutes ?? 10080,
    major_ack_minutes: version.major_ack_minutes ?? 120,
    major_closure_minutes: version.major_closure_minutes ?? 14400,
    minor_ack_minutes: version.minor_ack_minutes ?? 480,
    minor_closure_minutes: version.minor_closure_minutes ?? 20160,
    note: version.note || '',
  };
}

function buildPayload(form) {
  const effectiveFromIso = datetimeLocalToIso(form.effective_from);
  return {
    effective_from: effectiveFromIso,
    ...parseTatPolicyMinutesFromForm(form),
    note: form.note || undefined,
  };
}

function PolicyTable({ rows, variant = 'current' }) {
  return (
    <table className="tat-policy-table">
      <thead>
        <tr>
          <th className="col-severity">Severity</th>
          <th className="col-ack">Acknowledge within</th>
          <th className="col-close">Close within</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.severity} className={variant === 'scheduled' && row.changed ? 'tat-row-changed' : ''}>
            <td className="col-severity">{row.severity}</td>
            <td className="col-ack">
              {row.ack}
              {variant === 'scheduled' && row.changed && row.currentAck && (
                <span className="tat-diff-from">Current: {row.currentAck}</span>
              )}
            </td>
            <td className="col-close">
              {row.closure}
              {variant === 'scheduled' && row.changed && row.currentClosure && (
                <span className="tat-diff-from">Current: {row.currentClosure}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditHistoryTable({ entries }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!entries.length) {
    return <p className="tat-history-empty">No changes recorded yet.</p>;
  }

  const toggleDetails = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="tat-history-table-wrap">
      <table className="tat-history-table">
        <thead>
          <tr>
            <th className="col-when">When</th>
            <th className="col-action">Action</th>
            <th className="col-by">By</th>
            <th className="col-summary">Summary</th>
            <th className="col-details" aria-label="Details" />
          </tr>
        </thead>
        <tbody>
          {entries.slice(0, 20).map((entry) => {
            const visibleChanges = getTatAuditVisibleChanges(entry.changes);
            const summary = buildTatAuditSummary(entry);
            const actionClass = `tat-audit-action tat-audit-action--${entry.action_type}`;
            const isExpanded = expandedId === entry.id;
            const canExpand = visibleChanges.length > 0;

            return (
              <React.Fragment key={entry.id}>
                <tr className={isExpanded ? 'tat-history-row--expanded' : undefined}>
                  <td className="col-when">{formatDueInstant(entry.changed_at)}</td>
                  <td className="col-action">
                    <span className={actionClass}>
                      {formatTatAuditActionShort(entry.action_type)}
                    </span>
                  </td>
                  <td className="col-by">{entry.changed_by_name || '—'}</td>
                  <td className="col-summary" title={summary}>
                    <span className="tat-history-summary">{summary}</span>
                  </td>
                  <td className="col-details">
                    {canExpand ? (
                      <button
                        type="button"
                        className="tat-history-details-btn"
                        onClick={() => toggleDetails(entry.id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? 'Hide' : 'Details'}
                      </button>
                    ) : (
                      <span className="tat-history-details-placeholder">—</span>
                    )}
                  </td>
                </tr>
                {isExpanded && canExpand && (
                  <tr className="tat-history-details-row">
                    <td colSpan={5}>
                      {isTatAuditScheduledAgainstCurrent(entry) && (
                        <p className="tat-history-detail-hint">
                          Compared to the policy in effect when this change was scheduled.
                        </p>
                      )}
                      <table className="tat-history-detail-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>
                              {isTatAuditScheduledAgainstCurrent(entry)
                                ? 'Current policy'
                                : 'Before'}
                            </th>
                            <th>
                              {isTatAuditScheduledAgainstCurrent(entry)
                                ? 'Scheduled'
                                : 'After'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getTatAuditDetailTableRows(entry).map((row) => (
                            <tr key={`${entry.id}-${row.field}`}>
                              <td className="col-field">{row.field}</td>
                              <td className="col-before">{row.before}</td>
                              <td className="col-after">{row.after}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function ComplaintTatSettings({
  token,
  locationId,
  readOnly = false,
  compact = false,
  onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingMode, setEditingMode] = useState(null);
  const [summary, setSummary] = useState({ current: null, next: null });
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sumRes, auditRes] = await Promise.all([
        axios.get(API_ENDPOINTS.COMPLAINT_TAT.SUMMARY(locationId), { headers }),
        axios.get(API_ENDPOINTS.COMPLAINT_TAT.AUDIT(locationId), { headers }),
      ]);
      setSummary(sumRes.data?.data || { current: null, next: null });
      setAudit(auditRes.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load complaint TAT settings');
    } finally {
      setLoading(false);
    }
  }, [locationId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const startEditNew = () => {
    const effectiveFrom = defaultScheduleEffectiveLocal();
    setForm({
      ...versionToForm(summary.current, effectiveFrom),
      effective_from: effectiveFrom,
    });
    setEditingMode('new');
  };

  const startEditScheduled = () => {
    if (!summary.next) return;
    setForm(versionToForm(summary.next, defaultScheduleEffectiveLocal()));
    setEditingMode('scheduled');
  };

  const isSchedulingFuture = editingMode === 'scheduled' || (editingMode === 'new' && summary.current);

  const cancelEdit = () => setEditingMode(null);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMinutesChange = (field, value) => {
    handleChange(field, sanitizeTatMinutesInput(value));
  };

  const handleSave = async () => {
    if (!form.effective_from) {
      toast.error('Effective date is required');
      return;
    }
    if (isSchedulingFuture) {
      const scheduleErr = validateScheduleEffectiveLocal(form.effective_from);
      if (scheduleErr) {
        toast.error(scheduleErr);
        return;
      }
    }
    const effectiveFromIso = datetimeLocalToIso(form.effective_from);
    if (!effectiveFromIso) {
      toast.error('Invalid effective date and time');
      return;
    }
    const minutesErr = validateTatPolicyMinutes(form);
    if (minutesErr) {
      toast.error(minutesErr);
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = buildPayload(form);
      if (editingMode === 'scheduled' && summary.next?.id) {
        await axios.put(
          API_ENDPOINTS.COMPLAINT_TAT.VERSION(locationId, summary.next.id),
          payload,
          { headers },
        );
        toast.success('Scheduled TAT change updated');
      } else {
        await axios.post(API_ENDPOINTS.COMPLAINT_TAT.VERSIONS(locationId), payload, {
          headers,
        });
        toast.success(
          summary.current ? 'Complaint TAT change scheduled' : 'Complaint TAT configured',
        );
      }
      setEditingMode(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save complaint TAT');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteScheduled = async () => {
    if (!summary.next?.id) return;
    if (
      !window.confirm(
        'Remove this scheduled TAT change? The current policy will remain in effect.',
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(API_ENDPOINTS.COMPLAINT_TAT.VERSION(locationId, summary.next.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Scheduled TAT change removed');
      setEditingMode(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete scheduled change');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="complaint-tat-settings">
        <p className="tat-loading">Loading complaint TAT…</p>
      </div>
    );
  }

  const current = summary.current;
  const next = summary.next;
  const hasScheduled = Boolean(next);
  const editing = editingMode != null;
  const policyRows = formatPolicyMinutesRow(current);
  const scheduledRows = next ? formatPolicyDiffRows(current, next) : [];

  const renderEditor = () => (
    <div className="tat-editor-panel">
      <h4>
        {editingMode === 'scheduled' ? 'Edit scheduled change' : current ? 'Schedule new change' : 'Set up complaint TAT'}
      </h4>
      <div className="settings-form tat-editor-form">
        <div className="form-group">
          <label htmlFor="tat-effective-from">Effective from</label>
          <input
            id="tat-effective-from"
            type="datetime-local"
            value={form.effective_from}
            min={isSchedulingFuture ? minScheduleDatetimeLocal() : undefined}
            onChange={(e) => handleChange('effective_from', e.target.value)}
          />
          {isSchedulingFuture && (
            <p className="tat-editor-hint">
              Same-day scheduling is allowed; must be at least {TAT_SCHEDULE_MIN_LEAD_MINUTES}{' '}
              minutes from now.
            </p>
          )}
        </div>

        <div className="tat-editor-severity-grid">
          {SEVERITY_FIELDS.map(({ key, label }) => (
            <div key={key} className="tat-editor-severity-card">
              <h5>{label}</h5>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`${key}-ack`}>Acknowledgment (minutes)</label>
                  <input
                    id={`${key}-ack`}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    pattern="[1-9][0-9]*"
                    value={form[`${key}_ack_minutes`]}
                    onChange={(e) => handleMinutesChange(`${key}_ack_minutes`, e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`${key}-close`}>Resolution (minutes)</label>
                  <input
                    id={`${key}-close`}
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    pattern="[1-9][0-9]*"
                    value={form[`${key}_closure_minutes`]}
                    onChange={(e) => handleMinutesChange(`${key}_closure_minutes`, e.target.value)}
                  />
                </div>
              </div>
              <p className="tat-editor-hint">
                ≈ {formatMinutesAsDuration(form[`${key}_ack_minutes`])} ack ·{' '}
                {formatMinutesAsDuration(form[`${key}_closure_minutes`])} close
              </p>
            </div>
          ))}
        </div>

        <div className="form-group">
          <label htmlFor="tat-note">Note (optional)</label>
          <input
            id="tat-note"
            type="text"
            value={form.note}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="Reason or context for this change"
          />
        </div>

        <div className="form-actions">
          <button type="button" className="tat-btn tat-btn--secondary" onClick={cancelEdit} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="tat-btn tat-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingMode === 'scheduled' ? 'Save scheduled change' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`complaint-tat-settings ${compact ? 'complaint-tat-compact' : ''}`}>
      {!current && !editing && (
        <div className="tat-onboarding">
          <p className="tat-onboarding-required">
            Required onboarding: configure SLA targets before this location can accept complaints.
          </p>
          {!readOnly && (
            <button type="button" className="tat-btn tat-btn--primary" onClick={startEditNew}>
              Set up complaint TAT
            </button>
          )}
        </div>
      )}

      {(current || editing) && (
        <>
          {!editing && (
            <div className="tat-toolbar">
              <div className="tat-status-group">
                {current && (
                  <span className="tat-status-badge tat-status-badge--active">
                    Active since {formatDueInstant(current.effective_from)}
                  </span>
                )}
                {hasScheduled ? (
                  <span className="tat-status-badge tat-status-badge--scheduled">
                    Scheduled {formatDueInstant(next.effective_from)}
                  </span>
                ) : (
                  current && (
                    <span className="tat-status-badge tat-status-badge--none">No scheduled change</span>
                  )
                )}
              </div>
              {!readOnly && (
                <div className="tat-toolbar-actions">
                  <button
                    type="button"
                    className="tat-btn tat-btn--primary"
                    onClick={startEditNew}
                    disabled={hasScheduled}
                    title={
                      hasScheduled
                        ? 'Edit or delete the existing scheduled change first'
                        : 'Schedule a future policy version'
                    }
                  >
                    Schedule change
                  </button>
                </div>
              )}
            </div>
          )}

          {editing && !readOnly && renderEditor()}

          {!editing && current && (
            <div className={`tat-layout ${compact ? 'tat-layout--single' : ''}`}>
              <div className="tat-policy-column">
                <section className="tat-panel tat-panel--current">
                  <div className="tat-panel-header">
                    <div>
                      <h4>Current policy</h4>
                      <p className="tat-panel-subtitle">Applies to new complaints today</p>
                    </div>
                  </div>
                  <div className="tat-panel-body">
                    <PolicyTable rows={policyRows} variant="current" />
                  </div>
                </section>

                <section className="tat-panel tat-panel--scheduled">
                  <div className="tat-panel-header">
                    <div>
                      <h4>Scheduled change</h4>
                      <p className="tat-panel-subtitle">
                        {hasScheduled
                          ? `Takes effect ${formatDueInstant(next.effective_from)}`
                          : 'Plan the next policy update'}
                      </p>
                    </div>
                    {hasScheduled && !readOnly && (
                      <div className="tat-panel-actions">
                        <button type="button" className="tat-btn tat-btn--secondary" onClick={startEditScheduled}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="tat-btn tat-btn--danger"
                          onClick={handleDeleteScheduled}
                          disabled={deleting}
                        >
                          {deleting ? '…' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="tat-panel-body">
                    {hasScheduled ? (
                      <>
                        {next.note && <p className="tat-panel-note">{next.note}</p>}
                        <PolicyTable rows={scheduledRows} variant="scheduled" />
                      </>
                    ) : (
                      <p className="tat-panel-empty">
                        No upcoming change. Use &quot;Schedule change&quot; to set new targets that apply
                        from a future date and time.
                      </p>
                    )}
                  </div>
                </section>
              </div>

              <section className="tat-panel tat-panel--history">
                <div className="tat-panel-header">
                  <div>
                    <h4>Change history</h4>
                    <p className="tat-panel-subtitle">Recent policy updates</p>
                  </div>
                </div>
                <div className="tat-panel-body">
                  <AuditHistoryTable entries={audit} />
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ComplaintTatSettings;
